import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { UserRole, Client } from '../../types';
import * as api from '../../services/api';
import { 
  Search, 
  X, 
  Users, 
  CheckSquare, 
  Calendar, 
  MessageSquare, 
  CreditCard, 
  BookOpen, 
  Scroll, 
  Clock, 
  Settings, 
  LayoutDashboard, 
  Sparkles, 
  ArrowRight,
  PlusCircle,
  Network
} from 'lucide-react';

interface QuickCommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CommandItem {
  id: string;
  category: 'Klienten' | 'Navigation' | 'Aktionen';
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  onSelect: () => void;
}

export const QuickCommandPalette: React.FC<QuickCommandPaletteProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const isDomina = user?.role === UserRole.DOMINA;

  // Load clients if Domina
  useEffect(() => {
    if (isOpen && isDomina) {
      api.getClients()
        .then(data => setClients(data || []))
        .catch(err => console.warn('Could not load clients for command palette', err));
    }
  }, [isOpen, isDomina]);

  // Focus input on open
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Global keydown listener for Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const items = useMemo<CommandItem[]>(() => {
    const list: CommandItem[] = [];

    // 1. Navigation items
    if (isDomina) {
      list.push(
        {
          id: 'nav-dashboard',
          category: 'Navigation',
          title: 'Dashboard & Realm-Zentrale',
          subtitle: 'Übersicht, Aufmerksamkeit & Radar',
          icon: <LayoutDashboard className="w-4 h-4 text-primary" />,
          onSelect: () => { navigate('/'); onClose(); }
        },
        {
          id: 'nav-clients',
          category: 'Navigation',
          title: 'Klientenverwaltung',
          subtitle: 'Alle Klientenakten & Profile',
          icon: <Users className="w-4 h-4 text-emerald-400" />,
          onSelect: () => { navigate('/clients'); onClose(); }
        },
        {
          id: 'nav-tasks',
          category: 'Navigation',
          title: 'Aufgaben & Pflichten',
          subtitle: 'Aufgabenliste & Rituale',
          icon: <CheckSquare className="w-4 h-4 text-violet-400" />,
          onSelect: () => { navigate('/tasks'); onClose(); }
        },
        {
          id: 'nav-structure',
          category: 'Navigation',
          title: 'Struktur & Hierarchie',
          subtitle: 'Realm-Matrix & Lebensbereiche',
          icon: <Network className="w-4 h-4 text-cyan-400" />,
          onSelect: () => { navigate('/structure'); onClose(); }
        },
        {
          id: 'nav-calendar',
          category: 'Navigation',
          title: 'Kalender',
          subtitle: 'Termine & Rituale',
          icon: <Calendar className="w-4 h-4 text-amber-400" />,
          onSelect: () => { navigate('/calendar'); onClose(); }
        },
        {
          id: 'nav-messenger',
          category: 'Navigation',
          title: 'Flüsterraum / Messenger',
          subtitle: 'Direkte Kommunikation',
          icon: <MessageSquare className="w-4 h-4 text-rose-400" />,
          onSelect: () => { navigate('/messenger'); onClose(); }
        },
        {
          id: 'nav-payments',
          category: 'Navigation',
          title: 'Tribute & Finanzen',
          subtitle: 'Zahlungsübersicht & Buchungen',
          icon: <CreditCard className="w-4 h-4 text-emerald-400" />,
          onSelect: () => { navigate('/payments'); onClose(); }
        },
        {
          id: 'nav-diary',
          category: 'Navigation',
          title: 'Tagebuch & Berichte',
          subtitle: 'Klienteneinträge & Einreichungen',
          icon: <BookOpen className="w-4 h-4 text-blue-400" />,
          onSelect: () => { navigate('/diary'); onClose(); }
        },
        {
          id: 'nav-rules',
          category: 'Navigation',
          title: 'Regelwerke & Kodex',
          subtitle: 'Göttliche Gebote',
          icon: <Scroll className="w-4 h-4 text-amber-300" />,
          onSelect: () => { navigate('/rules'); onClose(); }
        },
        {
          id: 'nav-timer',
          category: 'Navigation',
          title: 'Timer & Wecker',
          subtitle: 'Countdown & Zeitnehmer',
          icon: <Clock className="w-4 h-4 text-orange-400" />,
          onSelect: () => { navigate('/timer'); onClose(); }
        },
        {
          id: 'nav-settings',
          category: 'Navigation',
          title: 'Einstellungen & Themes',
          subtitle: 'Farbpalette, Benachrichtigungen & Layout',
          icon: <Settings className="w-4 h-4 text-slate-400" />,
          onSelect: () => { navigate('/settings'); onClose(); }
        }
      );

      // Quick Actions for Domina
      list.push(
        {
          id: 'action-new-task',
          category: 'Aktionen',
          title: 'Neue Aufgabe vergeben',
          subtitle: 'Aufgabe für Klienten anlegen',
          icon: <PlusCircle className="w-4 h-4 text-primary" />,
          onSelect: () => { navigate('/tasks'); onClose(); }
        },
        {
          id: 'action-new-client',
          category: 'Aktionen',
          title: 'Neuen Klienten anlegen',
          subtitle: 'Neue Klientenakte einrichten',
          icon: <Sparkles className="w-4 h-4 text-emerald-400" />,
          onSelect: () => { navigate('/clients'); onClose(); }
        },
        {
          id: 'action-new-payment',
          category: 'Aktionen',
          title: 'Tribut / Zahlung verbuchen',
          subtitle: 'Finanztransaktion erfassen',
          icon: <CreditCard className="w-4 h-4 text-amber-400" />,
          onSelect: () => { navigate('/payments'); onClose(); }
        }
      );

      // Clients direct jump
      clients.forEach(c => {
        list.push({
          id: `client-${c.id}`,
          category: 'Klienten',
          title: c.name,
          subtitle: `${c.email || 'Keine E-Mail'} • ${c.isActive ? (c.isPaused ? 'Pausiert' : 'Aktiv') : 'Archiviert'}`,
          icon: <span className="w-4 h-4 flex items-center justify-center font-bold text-xs text-primary">👑</span>,
          onSelect: () => { navigate(`/clients/${c.id}`); onClose(); }
        });
      });
    } else {
      // Client role items
      list.push(
        {
          id: 'sub-dashboard',
          category: 'Navigation',
          title: 'Dashboard',
          subtitle: 'Heutige Aufgaben & Status',
          icon: <LayoutDashboard className="w-4 h-4 text-primary" />,
          onSelect: () => { navigate('/'); onClose(); }
        },
        {
          id: 'sub-tasks',
          category: 'Navigation',
          title: 'Meine Aufgaben',
          subtitle: 'Offene & fällige Pflichten',
          icon: <CheckSquare className="w-4 h-4 text-violet-400" />,
          onSelect: () => { navigate('/tasks'); onClose(); }
        },
        {
          id: 'sub-messenger',
          category: 'Navigation',
          title: 'Flüsterraum',
          subtitle: 'Kommunikation mit Göttin Cat',
          icon: <MessageSquare className="w-4 h-4 text-rose-400" />,
          onSelect: () => { navigate('/messenger'); onClose(); }
        },
        {
          id: 'sub-diary',
          category: 'Navigation',
          title: 'Tagebuch & Berichte',
          subtitle: 'Eintrag für heute verfassen',
          icon: <BookOpen className="w-4 h-4 text-blue-400" />,
          onSelect: () => { navigate('/diary'); onClose(); }
        },
        {
          id: 'sub-calendar',
          category: 'Navigation',
          title: 'Kalender',
          subtitle: 'Anstehende Aufgaben & Termine',
          icon: <Calendar className="w-4 h-4 text-amber-400" />,
          onSelect: () => { navigate('/calendar'); onClose(); }
        },
        {
          id: 'sub-rules',
          category: 'Navigation',
          title: 'Regelwerk',
          subtitle: 'Verbindliche Gebote',
          icon: <Scroll className="w-4 h-4 text-amber-300" />,
          onSelect: () => { navigate('/rules'); onClose(); }
        },
        {
          id: 'sub-timer',
          category: 'Navigation',
          title: 'Timer & Wecker',
          subtitle: 'Countdown-Tools',
          icon: <Clock className="w-4 h-4 text-orange-400" />,
          onSelect: () => { navigate('/timer'); onClose(); }
        },
        {
          id: 'sub-settings',
          category: 'Navigation',
          title: 'Einstellungen',
          subtitle: 'Theme & Benachrichtigungen',
          icon: <Settings className="w-4 h-4 text-slate-400" />,
          onSelect: () => { navigate('/settings'); onClose(); }
        }
      );
    }

    return list;
  }, [isDomina, clients, navigate, onClose]);

  // Filter items by search query
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(item => 
      item.title.toLowerCase().includes(q) || 
      (item.subtitle && item.subtitle.toLowerCase().includes(q)) ||
      item.category.toLowerCase().includes(q)
    );
  }, [items, query]);

  // Keyboard navigation inside list
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1 < filteredItems.length ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 >= 0 ? prev - 1 : filteredItems.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredItems[selectedIndex]) {
        filteredItems[selectedIndex].onSelect();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-start justify-center pt-14 md:pt-20 px-4 bg-background/80 backdrop-blur-md animate-fade-in"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-xl bg-surface/95 border border-border/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh] backdrop-blur-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Search Header */}
        <div className="flex items-center px-4 py-3.5 border-b border-border/40 gap-3">
          <Search className="w-5 h-5 text-primary shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder={isDomina ? "Klient suchen, Seite ansteuern oder Aktion tippen..." : "Bereich oder Funktion suchen..."}
            className="w-full bg-transparent text-text-main placeholder:text-text-muted text-sm sm:text-base outline-none"
          />
          {query ? (
            <button 
              onClick={() => setQuery('')}
              className="p-1 hover:bg-surface-light rounded-lg text-text-muted hover:text-text-main transition-colors cursor-pointer"
              title="Suchtext löschen"
            >
              <X className="w-4 h-4" />
            </button>
          ) : (
            <span className="hidden sm:inline-block text-[11px] font-mono px-2 py-0.5 rounded bg-surface-light text-text-muted border border-border/40">
              ESC zum Schließen
            </span>
          )}
        </div>

        {/* Results List */}
        <div className="overflow-y-auto p-2 space-y-1 divide-y divide-border/20">
          {filteredItems.length === 0 ? (
            <div className="py-10 text-center text-text-muted text-sm">
              Keine Treffer für &ldquo;{query}&rdquo; gefunden.
            </div>
          ) : (
            filteredItems.map((item, idx) => {
              const isSelected = idx === selectedIndex;
              return (
                <button
                  key={item.id}
                  onClick={item.onSelect}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`w-full text-left flex items-center justify-between px-3.5 py-2.5 rounded-xl transition-all cursor-pointer ${
                    isSelected 
                      ? 'bg-primary/15 text-text-main border border-primary/30 shadow-sm' 
                      : 'text-text-secondary hover:bg-surface-light/60 hover:text-text-main border border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-lg shrink-0 ${isSelected ? 'bg-primary/20 text-primary' : 'bg-surface-light text-text-muted'}`}>
                      {item.icon}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate text-text-main">
                        {item.title}
                      </p>
                      {item.subtitle && (
                        <p className="text-xs text-text-muted truncate">
                          {item.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded bg-surface-light/80 text-text-muted border border-border/30">
                      {item.category}
                    </span>
                    <ArrowRight className={`w-3.5 h-3.5 transition-transform ${isSelected ? 'text-primary translate-x-0.5' : 'text-text-muted opacity-0'}`} />
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer info */}
        <div className="px-4 py-2 bg-surface-light/40 border-t border-border/30 flex items-center justify-between text-[11px] text-text-muted">
          <span>Tipp: Mit <kbd className="font-mono bg-surface px-1 py-0.5 rounded border border-border/40 text-text-main">↑</kbd> <kbd className="font-mono bg-surface px-1 py-0.5 rounded border border-border/40 text-text-main">↓</kbd> navigieren &amp; <kbd className="font-mono bg-surface px-1 py-0.5 rounded border border-border/40 text-text-main">Enter</kbd> öffnen</span>
          <span>Fairyrealm Schnellzugriff</span>
        </div>
      </div>
    </div>
  );
};
